import { IconButton, Tooltip, Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import RateReviewIcon from "@mui/icons-material/RateReview";
import { useRecordContext } from "react-admin";
import { DocumentRead } from "@/types";
import { ReprocessDialog } from "./ReprocessDialog";
import { useState } from "react";

interface DocumentActionsProps {
  onReview?: (id: string) => void;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  onRetry?: (id: string, clientId?: string) => void;
  onDelete?: (id: string) => void;
  /**
   * Tooltip for the retry/reprocess button. Defaults to "Retry Processing",
   * which is the right words for a FAILED document and the wrong ones for a
   * document in review_required — there the action is a deliberate re-run, not
   * a retry of something that broke, and our own DOC-000001/DOC-000003 error
   * copy calls it "reprocess" (#6920). A reviewer told to reprocess should find
   * a control with that word on it.
   */
  retryLabel?: string;
}

/**
 * Action icon buttons for documents in the queue/list.
 * Visibility of each button is driven by which callbacks are provided.
 *
 * Every button carries an explicit `aria-label` (!4711 review). MUI's Tooltip
 * does not give an icon-only IconButton an accessible name, so screen readers
 * announced five unnamed buttons (WCAG 4.1.2, axe `button-name`). Pre-existing
 * across the whole row; fixed here because #6920 points error copy at one of
 * these controls by name, and a control our copy names should be findable.
 */
export const DocumentActions = ({
  onReview,
  onApprove,
  onReject,
  onRetry,
  onDelete,
  retryLabel = "Retry Processing",
}: DocumentActionsProps) => {
  const record = useRecordContext<DocumentRead>();
  const [reprocessOpen, setReprocessOpen] = useState(false);

  if (!record) return null;

  const handleClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  return (
    <>
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {onReview && (
          <Tooltip title="Review Document">
            <IconButton
              aria-label="Review Document"
              size="small"
              color="primary"
              onClick={(e) => handleClick(e, () => onReview(record.id))}
            >
              <RateReviewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {onApprove && (
          <Tooltip title="Accept">
            <IconButton
              aria-label="Accept"
              size="small"
              color="success"
              onClick={(e) => handleClick(e, () => onApprove(record.id))}
            >
              <CheckCircleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {onReject && (
          <Tooltip title="Reject">
            <IconButton
              aria-label="Reject"
              size="small"
              color="error"
              onClick={(e) => handleClick(e, () => onReject(record.id))}
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {onRetry && (
          <Tooltip title={retryLabel}>
            <IconButton
              aria-label={retryLabel}
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                setReprocessOpen(true);
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {onDelete && (
          <Tooltip title="Delete">
            <IconButton
              aria-label="Delete"
              size="small"
              color="error"
              onClick={(e) => handleClick(e, () => onDelete(record.id))}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {onRetry && reprocessOpen && (
        <ReprocessDialog
          open={reprocessOpen}
          onClose={() => setReprocessOpen(false)}
          record={record}
          onRetry={onRetry}
        />
      )}
    </>
  );
};
